'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { User, LogOut, LayoutDashboard, ChevronDown, Users, Image as ImageIcon, Lightbulb, Search, Settings, Flag, Upload } from 'lucide-react';

// ... imports
import { logoutAction } from '@/app/actions/auth-actions';

interface UserMenuProps {
    user?: {
        name: string;
        email: string;
        initials: string;
        avatarUrl?: string;
        handle?: string;
        role?: string;
    } | null;
}

export default function UserMenu({ user }: UserMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const isAdmin = user?.role === 'admin';
    const roleLabel =
        user?.role === 'admin'
            ? 'Admin'
            : user?.role === 'contributor'
                ? 'Contributor'
                : 'User';

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // If no user, show simple login link (or nothing)
    if (!user) {
        return (
            <Link
                href="/login"
                className="text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors px-3 py-2"
            >
                Sign In
            </Link>
        );
    }

    return (
        <div className="relative" ref={menuRef}>
            <button
                // ... (existing button attributes)
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 p-1 pl-3 pr-2 rounded-full border border-[var(--color-border)] bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] transition-all group"
            >
                <div className="text-right hidden sm:block">
                    <div className="text-xs font-semibold text-[var(--color-text-main)]">{user.name}</div>
                    <div className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">{roleLabel}</div>
                </div>

                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)] flex items-center justify-center shadow-lg group-hover:shadow-[var(--color-primary-glow)] transition-shadow">
                    {user.avatarUrl ? (
                        <img
                            src={user.avatarUrl}
                            alt={user.name}
                            className="w-full h-full object-cover rounded-full"
                            loading="lazy"
                        />
                    ) : (
                        <span className="text-xs font-bold text-white">{user.initials}</span>
                    )}
                </div>

                <ChevronDown size={14} className={`text-[var(--color-text-muted)] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-64 origin-top-right bg-[var(--color-bg-surface)] overflow-hidden shadow-2xl animate-fade-in z-50 p-0 border border-[var(--color-border)]">

                    {/* Header */}
                    <div className="p-4 border-b border-[var(--color-border)] bg-[rgba(255,255,255,0.02)]">
                        <p className="text-sm font-semibold text-[var(--color-text-main)]">{user.name}</p>
                        <p className="text-xs text-[var(--color-text-muted)] truncate">{user.email}</p>
                    </div>

                    {/* Menu Items */}
                    <div className="p-2">
                        {isAdmin ? (
                            <>
                                <Link
                                    href="/admin"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <LayoutDashboard size={16} />
                                    <span>Dashboard</span>
                                </Link>

                                <Link
                                    href="/admin/articles"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                        <polyline points="10 9 9 9 8 9"></polyline>
                                    </svg>
                                    <span>Articles</span>
                                </Link>

                                <Link
                                    href="/admin/interesting-facts"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Lightbulb size={16} />
                                    <span>Interesting Facts</span>
                                </Link>

                                <Link
                                    href="/admin/images"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <ImageIcon size={16} />
                                    <span>Images</span>
                                </Link>

                                <Link
                                    href="/admin/uploads"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Upload size={16} />
                                    <span>Uploads</span>
                                </Link>

                                <Link
                                    href="/admin/users"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Users size={16} />
                                    <span>Users</span>
                                </Link>
                                
                                <Link
                                    href="/admin/public-authors"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Users size={16} />
                                    <span>Public Authors</span>
                                </Link>
                                <Link
                                    href="/admin/seo-settings"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Search size={16} />
                                    <span>SEO Settings</span>
                                </Link>

                                <Link
                                    href="/admin/reported-content"
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Flag size={16} />
                                    <span>Reported Content</span>
                                </Link>
                            </>
                        ) : (
                            <>
                                <Link
                                    href={user.handle ? `/u/${user.handle}` : "/profile"}
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <User size={16} />
                                    <span>View profile</span>
                                </Link>

                                <Link
                                    href={user.handle ? `/u/${user.handle}/p` : "/profile"}
                                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Settings size={16} />
                                    <span>Edit profile</span>
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-2 border-t border-[var(--color-border)] bg-[rgba(255,255,255,0.02)]">
                        <form action={logoutAction}>
                            <button
                                type="submit"
                                className="flex w-full items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors"
                            >
                                <LogOut size={16} />
                                <span>Sign out</span>
                            </button>
                        </form>
                    </div>

                </div>
            )}
        </div>
    );
}
